/**
 * Local bot battle session state.
 * Manages the engine, replay recorder, and turn resolution for bot practice battles.
 */

import { BattleEngine } from "../engine/battle";
import { ReplayRecorder } from "../engine/replay";
import type { BattleAction, BotBattleConfig, PlayerAction, PlayerId, Replay, Team, TrainerProfile } from "../engine/types";

export type BotBattleSession = {
  engine: BattleEngine;
  recorder: ReplayRecorder;
  config: BotBattleConfig;
  playerSide: PlayerId;
  playerProfile: TrainerProfile;
  botProfile: TrainerProfile;
  botTeam: Team;
  isEnded: boolean;
  lastBotAction: BattleAction | null;
};

let currentSession: BotBattleSession | null = null;

/**
 * Create a local bot battle session from a BotBattleConfig.
 * The bot's team is resolved (built or randomized).
 */
export async function createBotBattleSession(
  config: BotBattleConfig,
  playerProfile: TrainerProfile,
  botProfileOverride?: TrainerProfile,
): Promise<BotBattleSession> {
  // Resolve bot team
  let botTeam: Team;
  if (config.botTeam) {
    botTeam = config.botTeam;
  } else if (config.bot.buildTeam) {
    botTeam = await config.bot.buildTeam();
  } else {
    // Randomize — dynamic import to avoid circular deps
    const { randomizeTeam } = await import("../engine/randomizer");
    botTeam = await randomizeTeam();
  }

  // Determine profiles
  const botProfile = botProfileOverride ?? config.bot.profile;
  botProfile.name = config.bot.name;

  // Create engine and recorder
  const engine = BattleEngine.create(config.battleConfig, config.seed, config.playerTeam, botTeam);
  const recorder = new ReplayRecorder(
    config.battleConfig,
    playerProfile,
    botProfile,
    config.playerTeam.members,
    botTeam.members,
  );
  recorder.setInitialState(engine.getState());

  const session: BotBattleSession = {
    engine,
    recorder,
    config,
    playerSide: "p1",
    playerProfile,
    botProfile,
    botTeam,
    isEnded: false,
    lastBotAction: null,
  };

  currentSession = session;
  return session;
}

/**
 * Submit the player's action, ask the bot for its action, resolve the turn,
 * and record it. Returns the updated session.
 */
export async function resolveBotTurn(
  session: BotBattleSession,
  playerAction: BattleAction,
): Promise<BotBattleSession> {
  if (session.isEnded) return session;

  const playerTurnAction: PlayerAction = { player: "p1", action: playerAction };

  // Get bot action
  const botAction = await session.config.bot.chooseAction(session.engine, "p2");
  const botTurnAction: PlayerAction = { player: "p2", action: botAction };

  // Submit and resolve
  session.engine.submitActions(playerTurnAction, botTurnAction);
  session.lastBotAction = botAction;
  const state = session.engine.getState();
  session.recorder.recordTurn(state.turn, [playerTurnAction, botTurnAction], state);

  if (session.engine.isEnded()) {
    session.isEnded = true;
  }

  return session;
}

/** Check if the bot's turn is ready (always true for synchronous bots). */
export function isBotTurnReady(_session: BotBattleSession): boolean {
  return true;
}

/** Get the current bot battle session (used for navigation). */
export function getBotBattleSession(): BotBattleSession | null {
  return currentSession;
}

/** Clear the current bot battle session. */
export function clearBotBattleSession(): void {
  currentSession = null;
}

/** Export the replay from a completed session. */
export function getBotReplay(session: BotBattleSession): Replay {
  return session.recorder.export();
}